import { AnalyticsFrequency } from '@constants'
import { keepPreviousData, QueryClient, QueryKey } from '@tanstack/react-query'
import { network } from '@utils'
import { AxiosResponse } from 'axios'
import { QueryFunction } from './root'

export type AnalyticsField = { field: string; value: number }

export type AnalyticsSerieEntry = { label: string; value: number }

export type AnalyticsSerie = { result: AnalyticsSerieEntry[] }

interface AnalyticsStatProps {
  field?: string
  aggregate?: string
  business_id: string
  date_from?: string
  date_to?: string
  date?: string
  account_type: string[]
  ignore_previous_profit?: boolean
}

interface AnalyticsSeriesProps extends AnalyticsStatProps {
  freq: AnalyticsFrequency
}

export const decorateExp = (s: string): string => [s, '*'].join('')

export const DASHBOARD_QUERY_STALE_TIME = 1000 * 60

export const dashboardQueryKey = (slug: string, ...parts: unknown[]): QueryKey => [
  'dashboard',
  slug,
  ...parts
]

export const dashboardQueryOptions = (slug: string) =>
  ({
    staleTime: DASHBOARD_QUERY_STALE_TIME,
    refetchOnMount: 'always' as const,
    refetchOnWindowFocus: false,
    placeholderData: ((previousData, previousQuery) =>
      previousQuery?.queryKey[1] === slug ? previousData : undefined) as typeof keepPreviousData
  } as const)

export const invalidateDashboardQueries = (queryClient: QueryClient, slug: string) =>
  queryClient.invalidateQueries({ queryKey: ['dashboard', slug] })

export const fetchEntryStat: QueryFunction<AnalyticsStatProps, AnalyticsField> = async props => {
  const { business_id, field = 'balance', aggregate = 'sum', ...rest } = props
  const url = `/v1/business/${business_id}/dashboard/entry-stat/`

  const { data } = await network.httpClient.request<
    any,
    AxiosResponse<{ results: AnalyticsField[] }>
  >({
    url,
    method: 'GET',
    params: { aggregate, field, ...rest }
  })

  const result = data.results.find(res => res.field === field)
  return result
}

export const fetchAccountStat: QueryFunction<AnalyticsStatProps, AnalyticsField> = async props => {
  const { business_id, field = 'balance', aggregate = 'sum', ...rest } = props
  const url = `/v1/business/${business_id}/dashboard/account-stat/`

  const { data } = await network.httpClient.request<
    any,
    AxiosResponse<{ results: AnalyticsField[] }>
  >({
    url,
    method: 'GET',
    params: { aggregate, field, ...rest }
  })

  const result = data.results.find(res => res.field === field)
  return result
}

export const fetchSeries: QueryFunction<
  AnalyticsSeriesProps,
  AnalyticsSerieEntry[]
> = async props => {
  const field = 'balance'
  const aggregate = 'sum'
  const { business_id, ...rest } = props
  const url = `/v1/business/${business_id}/dashboard/series/`

  const { data } = await network.httpClient.request<
    any,
    AxiosResponse<{ results: { field: string; result: AnalyticsSerieEntry[] }[] }>
  >({
    url,
    method: 'GET',
    params: { aggregate, field, ...rest }
  })

  const result = data.results.find(res => res.field === field).result

  return result
}

interface FetchFilterProps {
  slug: string
}

export type IDateRangeKey =
  | 'custom'
  | 'month-this'
  | 'month-prev'
  | 'year-this'
  | 'year-prev'
  | 'period-this'
  | 'period-prev'
  | 'all-history'

export interface IDashboardFilter {
  key: IDateRangeKey
  date_from?: string
  date_to?: string
}

export const fetchFilters: QueryFunction<FetchFilterProps, IDashboardFilter[]> = async ({
  slug
}) => {
  const url = `/v1/business/${slug}/dashboard/filters/`
  const { data } = await network.httpClient.request({
    url,
    method: 'GET'
  })
  return data
}

interface FetchAlertsProps {
  slug: string
}

export enum DashboardAlertIntents {
  SUGGESTION = 'SUGGESTION',
  INFO = 'INFO',
  SUCCESS = 'SUCCESS',
  WARNING = 'WARNING',
  ERROR = 'ERROR'
}

export enum DashboardAlertTypes {
  BANK_INTEGRATION_CREATION = 'BANK_INTEGRATION_CREATION',
  BANK_INTEGRATION_EXPIRED = 'BANK_INTEGRATION_EXPIRED',
  BANK_INTEGRATION_TO_BE_EXPIRED = 'BANK_INTEGRATION_TO_BE_EXPIRED',
  VAT_PERIOD_MUST_BE_REPORTED = 'VAT_PERIOD_MUST_BE_REPORTED',
  BANK_INTEGRATION_RENEWAL = 'BANK_INTEGRATION_RENEWAL',
  VAT_PERIOD_REPORTING_PENDING = 'VAT_PERIOD_REPORTING_PENDING',
  EU_RECAPITULATIVE_PENDING = 'EU_RECAPITULATIVE_PENDING',
  UNPAID_PURCHASE_INVOICES = 'UNPAID_PURCHASE_INVOICES',
  UNPAID_SALES_INVOICES = 'UNPAID_SALES_INVOICES',
  IF_INSURANCE_NEW_BUSINESS = 'IF_INSURANCE_NEW_BUSINESS',
  IF_INSURANCE_TENDERING = 'IF_INSURANCE_TENDERING',
  HOLVI_ACCOUNT_CREATION = 'HOLVI_ACCOUNT_CREATION',
  HOLVI_INTEGRATION_RENEWAL = 'HOLVI_INTEGRATION_RENEWAL',
  NEW_BUSINESS = 'NEW_BUSINESS',
  PLAN_RECOMMENDATION = 'PLAN_RECOMMENDATION'
}

export interface IDashboardAlert {
  intent: DashboardAlertIntents
  type: DashboardAlertTypes
  title: string
  message: string
  metadata?: {
    bank_integration_id?: number
    vat_period_id?: number
    period_id?: number
    monthly_average?: number
    recommended_plan?: string
    total_transactions?: number
  }
  link?: string
}

export const fetchAlerts: QueryFunction<FetchAlertsProps, IDashboardAlert[]> = async ({ slug }) => {
  const url = `/v1/business/${slug}/dashboard_alerts/`
  const { data } = await network.httpClient.request({
    url,
    method: 'GET'
  })
  return data
}

export type DashboardVatPeriodKind = 'MONTH' | 'QUARTER' | 'YEAR'

export type DashboardVatPeriodState =
  | 'reported'
  | 'locked'
  | 'open'
  | 'ongoing'
  | 'future'
  | 'missing'

export interface IDashboardVatPeriod {
  id: number | null
  start_date: string
  end_date: string
  due_date: string | null
  is_locked: boolean
  is_reported: boolean
  placeholder: boolean
  current: boolean
  state: DashboardVatPeriodState
  period: DashboardVatPeriodKind
}

export interface IDashboardVatYear {
  period: DashboardVatPeriodKind
  period_count: number
  reported_count: number
  year: number
  start_date: string
  end_date: string
  vat_periods: IDashboardVatPeriod[]
}

export interface IDashboardFinancialYear {
  period_id: number
  start_date: string
  end_date: string
  is_locked: boolean
}

export enum DashboardDeadlineType {
  VAT_RETURN = 'VAT_RETURN',
  PERIOD_REPORT = 'PERIOD_REPORT',
  EU_RECAPITULATIVE = 'EU_RECAPITULATIVE'
}

export interface IDashboardDeadline {
  type: DashboardDeadlineType
  date: string
  days_left: number
  reference_id: number
  start_date?: string
  end_date?: string | null
}

interface IDashboardAmountBucket {
  amount: number
  count: number
}

export interface IDashboardInvoiceSummary {
  overdue: IDashboardAmountBucket
  awaiting: IDashboardAmountBucket
  oldest_overdue_days: number | null
}

export interface IDashboardDocumentSummary {
  unbooked_count: number
  suggested_count: number
  draft_count: number
}

export interface IDashboardOverview {
  date: string
  financial_year: IDashboardFinancialYear | null
  vat_year: IDashboardVatYear | null
  deadlines: IDashboardDeadline[]
  documents: IDashboardDocumentSummary
  invoices: IDashboardInvoiceSummary | null
  purchase_invoices?: IDashboardInvoiceSummary | null
}

interface FetchOverviewProps {
  slug: string
}

export const fetchDashboardOverview: QueryFunction<
  FetchOverviewProps,
  IDashboardOverview
> = async ({ slug }) => {
  const url = `/v1/business/${slug}/dashboard/overview/`
  const { data } = await network.httpClient.request({
    url,
    method: 'GET'
  })
  return data
}

export interface IDashboardBootstrap {
  overview: IDashboardOverview
  alerts: IDashboardAlert[]
}

export const fetchDashboardBootstrap: QueryFunction<
  FetchOverviewProps,
  IDashboardBootstrap
> = async ({ slug }) => {
  const url = `/v1/business/${slug}/dashboard/bootstrap/`
  const { data } = await network.httpClient.request({
    url,
    method: 'GET'
  })
  return data
}

export interface IAccountStatSpec {
  key: string
  account_type: string[]
  date_from?: string
  date_to?: string
  date?: string
}

interface AccountStatBatchProps {
  slug: string
  specs: IAccountStatSpec[]
}

export const fetchAccountStatBatch: QueryFunction<
  AccountStatBatchProps,
  Record<string, number>
> = async ({ slug, specs }) => {
  const url = `/v1/business/${slug}/dashboard/account-stat/batch/`
  const { data } = await network.httpClient.request<
    any,
    AxiosResponse<{ results: Record<string, number> }>
  >({
    url,
    method: 'GET',
    params: { specs: JSON.stringify(specs) }
  })
  return data.results ?? {}
}

export interface ISeriesSpec {
  key: string
  account_type: string[]
  date_from: string
  date_to: string
  freq: AnalyticsFrequency
}

interface SeriesBatchProps {
  slug: string
  specs: ISeriesSpec[]
}

export const fetchSeriesBatch: QueryFunction<
  SeriesBatchProps,
  Record<string, AnalyticsSerieEntry[]>
> = async ({ slug, specs }) => {
  const url = `/v1/business/${slug}/dashboard/series/batch/`
  const { data } = await network.httpClient.request<
    any,
    AxiosResponse<{ results: Record<string, AnalyticsSerieEntry[]> }>
  >({
    url,
    method: 'GET',
    params: { specs: JSON.stringify(specs) }
  })
  return data.results ?? {}
}
